import { assertNever } from "helpers/assertNever";
import type { TemplateResult } from "lit";
import { html } from "lit";
import { platformConfigs, type SupportedPlatform } from "ui-common/socialMediaConfig";

export type IconVariant = "colorful" | "blackAndWhite";

/**
 * Get a social media icon for a specific platform
 * @param platform - The social media platform
 * @param variant - Whether to use colorful (PNG) or black-and-white (Phosphor) icons
 * @returns TemplateResult for Phosphor icons or string URL for PNG icons
 */
export function getSocialIcon(
  platform: SupportedPlatform,
  variant: IconVariant = "blackAndWhite",
): TemplateResult | string {
  const config = platformConfigs.find((p) => p.platform === platform);

  if (!config) {
    // Fallback for unknown platforms
    return html`<ph-link class="social-link-icon" size="16" weight="fill"></ph-link>`;
  }

  if (variant === "colorful") {
    return config.colorfulIcon;
  }

  if (variant === "blackAndWhite") {
    return config.blackAndWhiteIcon;
  }

  return assertNever(variant);
}

/**
 * Pre-built mapping of platforms to their black-and-white Phosphor icons
 * Useful for components that need direct access to the mapping
 */
export const SOCIAL_ICON_MAP: Record<SupportedPlatform, TemplateResult> = platformConfigs.reduce<
  Record<SupportedPlatform, TemplateResult>
>(
  (acc, config) => {
    acc[config.platform] = config.blackAndWhiteIcon;
    return acc;
  },
  {} as Record<SupportedPlatform, TemplateResult>,
);

/**
 * Default fallback icon for unsupported platforms
 */
export const DEFAULT_SOCIAL_ICON = html`<ph-link class="social-link-icon" size="16" weight="fill"></ph-link>`;
